"use client";

import React, { useEffect, useRef, useState } from "react";
import { Activity, Database, MessageSquare, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const categories = ["用户基本概况", "应用交互行为", "安全访问日志", "个性化偏好", "设备与系统信息", "大致地理位置", "服务改进反馈", "功能订阅状态"];
const dataPoints = [{
  title: "系统运行与性能监控",
  description: "为了保障应用运行的稳定性，我们会收集脱敏后的崩溃日志和加载速度数据。这些信息帮助我们的工程团队快速定位潜在问题，确保持续为您提供流畅、高效、零延迟的服务环境。",
  icon: Activity
}, {
  title: "个性化体验深度定制",
  description: "通过分析您的使用偏好，我们能够为您提供量身定制的内容推荐和功能布局。这些数据仅用于提升您的个人使用体验，不会在未经授权的情况下与任何第三方广告商共享或出售。",
  icon: Users
}, {
  title: "端到端安全加密存储",
  description: "您的所有敏感信息在传输和存储过程中均采用行业领先的端到端加密技术。我们确保存储在云端的数据即使在极端情况下也无法被第三方非法解密，保障您的数字资产绝对安全。",
  icon: Database
}, {
  title: "合规性审计与隐私保护",
  description: "我们严格遵守全球数据保护条例。通过定期进行的内部审计和第三方安全评估，确保我们的数据处理流程始终透明、合规且符合最高道德标准，让您在享受服务时无后顾之忧。",
  icon: MessageSquare
}];
function DataCard({
  point,
  index
}: {
  point: typeof dataPoints[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div data-section-id="147721" ref={ref} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="bg-card rounded-[24px] border-none shadow-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl overflow-hidden group">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-[#58CC02]/10 text-[#58CC02] group-hover:bg-[#58CC02] group-hover:text-primary-foreground transition-colors duration-300">
              <point.icon className="w-6 h-6" />
            </div>
            <CardTitle className="text-xl font-default font-semibold tracking-tight">
              {point.title}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground font-default leading-[1.6] text-[18px]">
            {point.description}
          </p>
        </CardContent>
      </Card>
    </div>;
}
export default function CollecteDeDonnees() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="信息使用与存储" className="py-24 lg:py-[120px] bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#58CC02]/5 via-background to-background pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {}
          <div className="lg:col-span-4 lg:sticky lg:top-32 space-y-8">
            <div ref={headerRef} className={`transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
              <h2 className="text-[32px] font-default font-bold tracking-tight mb-4">
                我们如何使用与存储信息
              </h2>
              <p className="text-muted-foreground font-default leading-[1.6] text-[18px] mb-8">
                我们的方法基于多维度的信息整合，致力于为您构建一个透明、安全且高度个性化的数字生态系统。
              </p>
              
              <div className="flex flex-wrap gap-3">
                {categories.map((category, i) => <Badge key={i} data-index={i} variant="outline" className="border-primary/20 text-foreground hover:border-[#58CC02] hover:bg-[#58CC02]/5 transition-colors py-1.5 px-4 text-sm font-default font-medium rounded-full">
                    {category}
                  </Badge>)}
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8 space-y-6">
            {dataPoints.map((point, i) => <DataCard key={i} point={point} index={i} />)}
          </div>

        </div>
      </div>
    </section>;
}